<!-- eslint-disable max-len -->
<template>
  <div class="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_280px]">
    <!-- Left column: main + optional cards -->
    <div>
      <!-- Main highlight card: auto-message -->
      <div class="rounded-2xl border-t-4 border-teal-500 bg-white p-4 shadow-sm ring-1 ring-gray-200">
        <div class="flex items-start gap-3">
          <span class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
            <PaperAirplaneIcon class="size-5" />
          </span>
          <div class="min-w-0 flex-1">
            <p class="font-semibold text-gray-900">
              {{ t('livvia.prepare.messageCard.title') }}
            </p>
            <p class="mt-0.5 text-xs text-gray-500">
              {{ t('livvia.prepare.messageCard.description') }}
            </p>
          </div>
          <Switch
            v-model="livvia.autoMessageEnabled"
            :class="[livvia.autoMessageEnabled ? 'bg-teal-600' : 'bg-gray-300', 'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600']"
          >
            <span class="sr-only">{{ t('livvia.prepare.messageCard.toggle') }}</span>
            <span
              aria-hidden="true"
              :class="[livvia.autoMessageEnabled ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block size-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out']"
            />
          </Switch>
        </div>

        <div class="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm leading-relaxed text-gray-700">
          {{ t('livvia.prepare.messageCard.preview') }}
          <span class="font-semibold text-teal-700">livelink.com.br/r/{{ shortHandle }}</span>
        </div>

        <div class="mt-3 flex flex-wrap gap-2">
          <span
            v-for="chip in chips"
            :key="chip"
            class="inline-flex items-center gap-1 rounded-full border border-teal-100 bg-teal-50 px-3 py-1 text-xs font-semibold text-teal-700"
          >
            <CheckIcon class="size-3" />
            {{ chip }}
          </span>
        </div>
      </div>

      <!-- Row of 2 optional cards -->
      <div class="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div class="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-200">
          <div class="flex items-start gap-3">
            <span class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-500">
              <ChatBubbleLeftRightIcon class="size-5" />
            </span>
            <div class="min-w-0">
              <p class="font-semibold text-gray-900">
                {{ t('livvia.prepare.autoReplies.title') }}
                <span class="ml-1 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-gray-500">{{ t('livvia.prepare.optional') }}</span>
              </p>
              <p class="mt-0.5 text-xs text-gray-500">
                {{ t('livvia.prepare.autoReplies.description') }}
              </p>
            </div>
          </div>
        </div>

        <div class="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-200">
          <div class="flex items-start gap-3">
            <span class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-500">
              <TableCellsIcon class="size-5" />
            </span>
            <div class="min-w-0">
              <p class="font-semibold text-gray-900">
                {{ t('livvia.prepare.products.title') }}
                <span class="ml-1 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-gray-500">{{ t('livvia.prepare.optional') }}</span>
              </p>
              <p class="mt-0.5 text-xs text-gray-500">
                {{ t('livvia.prepare.products.description') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right column: phone preview -->
    <div class="order-first flex flex-col items-center gap-2 lg:order-none">
      <p class="text-[10px] font-bold uppercase tracking-wide text-gray-400">
        {{ t('livvia.prepare.phone.note') }}
      </p>
      <div class="w-full max-w-[240px] overflow-hidden rounded-3xl border-[6px] border-gray-900 bg-white shadow-lg">
        <div class="bg-gradient-to-br from-teal-400 to-teal-700 px-3 py-3 text-center text-xs font-semibold text-white">
          {{ t('livvia.prepare.phone.header', { handle: shortHandle }) }}
        </div>
        <div class="flex border-b border-gray-200 text-[11px] font-semibold">
          <span class="flex-1 border-b-2 border-teal-600 py-2 text-center text-teal-700">
            {{ t('livvia.prepare.phone.tabData') }}
          </span>
          <span class="flex-1 py-2 text-center text-gray-400">
            {{ t('livvia.prepare.phone.tabCart') }}
          </span>
        </div>
        <div class="space-y-2 p-3">
          <p class="text-[11px] leading-snug text-gray-700">
            {{ t('livvia.prepare.phone.greeting') }}
          </p>
          <div
            v-for="f in phoneFields"
            :key="f"
          >
            <p class="text-[9px] font-bold uppercase tracking-wide text-gray-400">
              {{ f }}
            </p>
            <div class="mt-1 h-6 rounded-md border border-gray-200 bg-gray-50" />
          </div>
          <div class="mt-1 rounded-lg bg-teal-600 py-2 text-center text-[11px] font-semibold text-white">
            {{ t('livvia.prepare.phone.btn') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Switch } from '@headlessui/vue';
import {
  PaperAirplaneIcon,
  ChatBubbleLeftRightIcon,
  TableCellsIcon,
  CheckIcon,
} from '@heroicons/vue/24/outline';
import useAuthStore from '@/store/auth';
import useLivviaStore from '@/store/livvia';

const { t } = useI18n();
const { session } = useAuthStore();
const livvia = useLivviaStore();

const shortHandle = computed(() => {
  const name = session?.name?.split(' ')[0] || 'voce';
  return name.toLowerCase().replace(/[^a-z0-9]/g, '');
});

const chips = computed(() => [
  t('livvia.prepare.messageCard.chips.name'),
  t('livvia.prepare.messageCard.chips.whatsapp'),
  t('livvia.prepare.messageCard.chips.cpf'),
  t('livvia.prepare.messageCard.chips.address'),
]);

const phoneFields = computed(() => [
  t('livvia.prepare.phone.fieldName'),
  t('livvia.prepare.phone.fieldWhatsapp'),
  t('livvia.prepare.phone.fieldCpf'),
  t('livvia.prepare.phone.fieldAddress'),
]);
</script>
